import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LogoLockup } from "@/components/Logo";
import { ArrowLeft, MailCheck, Copy } from "lucide-react";

/**
 * Forgot Password page.
 * - Always shows a generic success message (backend never leaks account existence).
 * - If backend responds with `dev_reset_url`, we surface it to the user AND copy it.
 *   That block is only present when Resend email delivery isn't configured yet.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [devUrl, setDevUrl] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
      if (data.dev_reset_path) {
        setDevUrl(`${window.location.origin}${data.dev_reset_path}`);
      }
      toast.success("If an account exists, a reset link is on the way.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't process request. Try again.");
    } finally { setLoading(false); }
  };

  const copyDevUrl = async () => {
    try {
      await navigator.clipboard.writeText(devUrl);
      toast.success("Reset link copied to clipboard");
    } catch { toast.error("Copy failed — long-press the link to select and copy."); }
  };

  return (
    <div className="min-h-screen flex items-stretch bg-[#FBF5EA]" data-testid="forgot-password-page">
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="max-w-md w-full">
          <Link to="/login" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mb-6">
            <ArrowLeft size={14} /> Back to sign in
          </Link>
          <LogoLockup size={44} />
          <h1 className="mt-8 font-display font-black text-4xl sm:text-5xl tracking-tighter">Forgot your password?</h1>
          <p className="mt-3 text-slate-600 text-sm">
            No worries — enter the email you signed up with, and we'll send a link to reset it.
          </p>

          {!sent ? (
            <form onSubmit={submit} className="mt-8 space-y-5">
              <div>
                <Label htmlFor="email" className="text-xs tracking-widest uppercase font-bold">Email</Label>
                <Input
                  data-testid="forgot-email"
                  id="email"
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="mt-1 h-12"
                />
              </div>
              <Button
                data-testid="forgot-submit"
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-full bg-orange-800 hover:bg-orange-900 text-white active:scale-95 transition-transform"
              >
                {loading ? "Sending…" : "Send reset link"}
              </Button>
            </form>
          ) : (
            <div className="mt-8" data-testid="forgot-sent-panel">
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 flex gap-3">
                <MailCheck className="text-emerald-600 flex-shrink-0 mt-0.5" size={22} />
                <div className="text-sm text-emerald-900">
                  <div className="font-bold">Check your inbox</div>
                  <p className="mt-1">If an account exists for <span className="font-mono">{email}</span>, we've just sent a password reset link to it. The link expires in 60 minutes.</p>
                </div>
              </div>

              {devUrl && (
                <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-xs" data-testid="dev-reset-url-panel">
                  <div className="font-bold text-amber-900">Admin support mode</div>
                  <p className="mt-1 text-amber-900">Email provider isn't wired up yet, so here's the direct link so an admin can share it via WhatsApp/SMS:</p>
                  <div className="mt-2 flex items-center gap-2 bg-white border border-amber-200 rounded-lg p-2">
                    <div className="flex-1 font-mono text-[10px] break-all text-amber-900">{devUrl}</div>
                    <button
                      onClick={copyDevUrl}
                      data-testid="dev-reset-copy"
                      className="flex-shrink-0 h-7 px-2 rounded-md bg-amber-800 hover:bg-amber-900 text-white flex items-center gap-1 text-[10px]"
                    >
                      <Copy size={11} /> Copy
                    </button>
                  </div>
                </div>
              )}

              <div className="mt-6 text-sm text-slate-600">
                Didn't get it? Check spam, or{" "}
                <button onClick={() => { setSent(false); setDevUrl(""); }} className="text-orange-800 font-semibold hover:underline">try again</button>.
              </div>
            </div>
          )}

          <div className="mt-10 text-sm text-slate-600">
            Remember your password?{" "}
            <Link to="/login" className="text-orange-800 font-semibold hover:underline">Sign in</Link>
          </div>
        </div>
      </div>

      <div className="hidden lg:block flex-1 bg-orange-800 relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1783254877023-f27a32c7e8bb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxpbmRpYW4lMjBzdHVkZW50JTIwc3R1ZHlpbmclMjBob3N0ZWx8ZW58MHx8fHwxNzgzNjU5OTc2fDA&ixlib=rb-4.1.0&q=85" className="w-full h-full object-cover opacity-70" alt="" />
        <div className="absolute inset-0 bg-orange-900/40" />
        <div className="absolute bottom-16 left-16 right-16 text-white">
          <div className="text-xs tracking-[0.25em] uppercase font-bold text-amber-400">Locked out?</div>
          <div className="font-display font-black text-4xl tracking-tighter mt-2">One email away from getting back in.</div>
        </div>
      </div>
    </div>
  );
}
